/**
 * src/services/spatial/deck/layerRegistry.ts
 *
 * Centralised deck.gl layer registry. Instead of scattering
 * `new SomeLayer(...)` calls across unrelated components, every deck
 * layer GeoTrust renders is built here from the normalised
 * `GeoTrustMapState`. This is the seam later phases extend
 * (CountryPolicyLayer, ExecutionRuleLayer, AgentPositionLayer, …).
 *
 * Phase 1 implements a single `SpatialObjectLayer` (a `GeoJsonLayer`)
 * fed by GeoLink's `/api/spatial-objects/render` FeatureCollection.
 *
 * Pure + framework-free: the React overlay (`SpatialDeckOverlay`) calls
 * `buildDeckLayers()` and hands the result to the shared
 * `DeckOverlayController`. Keeping it pure makes it trivially unit
 * testable (feed a state, assert the layer props).
 */

import { GeoJsonLayer, ScatterplotLayer } from '@deck.gl/layers';
import type { GeoJsonLayerProps } from '@deck.gl/layers';
import type { Layer, PickingInfo } from '@deck.gl/core';
import type { SpatialObjectFeature } from '../geoLinkTypes';
import type { MapFeature } from '../map/mapTypes';
import type { GeoTrustMapState, MapEntityType } from '../../../components/spatial/MapDataContext';
import {
  getSpatialElevation,
  getSpatialFillColor,
  getSpatialLineColor,
  getPolicyStateColor,
  SEMANTIC_RGB,
  type SelectionState,
  type RGBA,
} from '../../../components/spatial/spatialSemantics';

/** Stable deck layer ids (also used as `visibleLayerIds` entries). */
export const DECK_LAYER_IDS = {
  spatialObjects: 'geotrust-spatial-objects',
  // Reserved for later phases (kept here so the id namespace is central):
  countryPolicy:  'geotrust-country-policy',
  executionRules: 'geotrust-execution-rules',
  ruleTriggers:   'geotrust-rule-triggers',
  agentPositions: 'geotrust-agent-positions',
  agentAuthority: 'geotrust-agent-authority',
  agentPaths:     'geotrust-agent-paths',
  presenceCells:  'geotrust-presence-cells',
  liveSessions:   'geotrust-live-sessions',
  transactions:   'geotrust-transactions',
  dronePositions: 'geotrust-drone-positions',
  dronePaths:     'geotrust-drone-paths',
  activity:       'geotrust-activity',
} as const;

/** Zoom thresholds for zoom-dependent visibility / detail. */
export const ZOOM = {
  /** Below this, hide spatial objects entirely (they're sub-pixel). */
  spatialMinVisible: 3,
  /** At/above this, extrude polygons in 3D. Below → flat fill (cheaper). */
  spatialExtrude: 14,
} as const;

export interface LayerBuildContext {
  state: GeoTrustMapState;
  /** Current basemap zoom (for zoom-dependent visibility/detail). */
  zoom: number;
  /** Reduced-detail mode for low-powered devices — disables extrusion. */
  reducedDetail?: boolean;
  /** Master enable flag from the spatial-objects controls. */
  spatialEnabled?: boolean;
  /**
   * Whether the deck overlay owns the spatial-objects layer. False when the
   * legacy pipeline renders spatial objects through native Mapbox
   * fill-extrusion — the overlay then draws ONLY the aggregate layers
   * (country policy, rules, presence, agents, drones). Defaults true.
   */
  renderSpatialObjects?: boolean;
  /** Spatial-object picking (Phase 1 path → the detail panel). */
  onHover?: (info: PickingInfo) => void;
  onClick?: (info: PickingInfo) => void;
  /** Generic picking for the Phase 2 map-API layers. */
  onEntityHover?: (info: PickingInfo, layerId: string, entityType: MapEntityType) => void;
  onEntityClick?: (info: PickingInfo, layerId: string, entityType: MapEntityType) => void;
}

/**
 * deck's GeoJsonLayer `data` prop demands a strict GeoJSON geometry
 * union; our `MapFeature.geometry.type` is a loose `string`, so we cast
 * the slice through this alias (deck reads geometry structurally at
 * runtime — the widening is safe).
 */
type DeckGeoJsonData = GeoJsonLayerProps['data'];
const asGeoJsonData = (features: MapFeature[]): DeckGeoJsonData =>
  features as unknown as DeckGeoJsonData;

/** Whether a layer is visible + authorized (not 401/403). */
function layerActive(state: GeoTrustMapState, layerId: string): boolean {
  if (!state.visibleLayerIds.includes(layerId)) return false;
  if (state.layerStatus[layerId]?.permissionDenied) return false;
  return true;
}

/** Extract a [lng,lat] from a Point-ish map feature (else null). */
function pointCoord(f: MapFeature): [number, number] | null {
  const g = f?.geometry;
  if (g && g.type === 'Point' && Array.isArray(g.coordinates)) {
    const c = g.coordinates as number[];
    if (Number.isFinite(c[0]) && Number.isFinite(c[1])) return [Number(c[0]), Number(c[1])];
  }
  return null;
}

/** Normalized-policy state for a country feature (effectiveState → policyState). */
function policyStateOf(f: MapFeature): string | null {
  const p = f?.properties as Record<string, unknown> | undefined;
  return (p?.effectiveState as string) ?? (p?.policyState as string) ?? null;
}

/** Execution-rule status → semantic colour. */
function ruleColor(f: MapFeature): RGBA {
  const status = String((f?.properties as Record<string, unknown>)?.status ?? '').toLowerCase();
  if (status.includes('trigger')) return [...SEMANTIC_RGB.ruleTriggered, 200] as RGBA;
  if (status.includes('fail'))    return [...SEMANTIC_RGB.warning, 200] as RGBA;
  if (status.includes('disable') || status.includes('inactive')) return [...SEMANTIC_RGB.inactive, 160] as RGBA;
  return [...SEMANTIC_RGB.active, 190] as RGBA;
}

/** Presence density ramp by aggregate count (grey→pink), aggregate-only. */
function presenceColor(f: MapFeature): RGBA {
  const raw = (f?.properties as Record<string, unknown>)?.aggregateCount
    ?? (f?.properties as Record<string, unknown>)?.count;
  const n = Number(raw);
  const t = Number.isFinite(n) ? Math.max(0, Math.min(1, Math.log10(n + 1) / 3)) : 0; // ~1..1000
  const [r, g, b] = SEMANTIC_RGB.live;
  return [r, g, b, Math.round(60 + t * 150)] as RGBA;
}

function selectionState(state: GeoTrustMapState): SelectionState {
  return {
    selectedId: state.selectedEntity?.entityType === 'spatialObject' ? state.selectedEntity.id : null,
    hoveredId: state.hoveredEntity?.entityType === 'spatialObject' ? state.hoveredEntity.id : null,
  };
}

/**
 * Build the Spatial Object layer (Phase 1). Returns `null` when the
 * layer shouldn't render (hidden, disabled, zoomed too far out) so the
 * registry can omit it.
 */
export function buildSpatialObjectLayer(ctx: LayerBuildContext): Layer | null {
  const { state, zoom, reducedDetail } = ctx;

  if (ctx.spatialEnabled === false) return null;
  if (!state.visibleLayerIds.includes(DECK_LAYER_IDS.spatialObjects)) return null;
  if (zoom < ZOOM.spatialMinVisible) return null;

  const sel = selectionState(state);
  const extruded = !reducedDetail && zoom >= ZOOM.spatialExtrude;

  // deck.gl's GeoJsonLayer generic is the *properties* type, and it
  // widens each feature's geometry to GeoJSON's `number[]` positions —
  // which is incompatible with our stricter tuple-typed
  // `SpatialObjectFeature`. So we don't parametrise the layer and
  // instead narrow inside the accessors (deck passes the whole feature
  // as `object`).
  const asFeature = (f: unknown): SpatialObjectFeature => f as SpatialObjectFeature;

  return new GeoJsonLayer({
    id: DECK_LAYER_IDS.spatialObjects,
    data: state.spatialObjects,
    pickable: true,
    stroked: true,
    filled: true,
    extruded,
    wireframe: false,
    // Absolute top height in metres. GeoJsonLayer always extrudes from the
    // ground (no fill-extrusion-base), so floating tiers cannot be drawn as
    // slabs here — SpatialObjectsMapLayer paints buildings with native
    // Mapbox fill-extrusion instead. Do NOT add base+height: heightMeters is
    // already the absolute top (adding base double-counted and made towers
    // like Atlantis The Royal look ~2× too tall).
    getElevation: (f: unknown) =>
      extruded ? getSpatialElevation(asFeature(f)) : 0,
    getFillColor: (f: unknown) => getSpatialFillColor(asFeature(f), sel),
    getLineColor: (f: unknown) => getSpatialLineColor(asFeature(f), sel),
    getLineWidth: 1,
    lineWidthUnits: 'pixels',
    lineWidthMinPixels: 1,
    pointRadiusUnits: 'pixels',
    getPointRadius: 6,
    pointRadiusMinPixels: 4,
    opacity: 0.85,
    autoHighlight: true,
    highlightColor: [56, 189, 248, 90], // sky-400 wash
    material: false,
    // Recompute colours only when selection/hover changes (not on every
    // camera move) so we don't thrash the GPU attribute buffers.
    updateTriggers: {
      getFillColor: [sel.selectedId, sel.hoveredId],
      getLineColor: [sel.selectedId, sel.hoveredId],
      getElevation: [extruded],
    },
    onHover: ctx.onHover,
    onClick: ctx.onClick,
  });
}

function entityPickHandlers(ctx: LayerBuildContext, layerId: string, entityType: MapEntityType) {
  return {
    onHover: ctx.onEntityHover ? (info: PickingInfo) => ctx.onEntityHover!(info, layerId, entityType) : undefined,
    onClick: ctx.onEntityClick ? (info: PickingInfo) => ctx.onEntityClick!(info, layerId, entityType) : undefined,
  };
}

/** Country-policy choropleth (poll layer). Colour = normalized effectiveState. */
export function buildCountryPolicyLayer(ctx: LayerBuildContext): Layer | null {
  const { state } = ctx;
  if (!layerActive(state, DECK_LAYER_IDS.countryPolicy)) return null;
  // Fill only — no stroke. The main map already draws country boundaries
  // (native `countries-outline` line layer). Stroking here too painted a
  // SECOND outline over each border that diverged from the native one when
  // zoomed in (different geometry source/simplification), showing up as
  // "double" / dotted borders. Let the native overlay own the boundary line
  // and keep just the normalized policy-state choropleth fill here.
  return new GeoJsonLayer({
    id: DECK_LAYER_IDS.countryPolicy,
    data: asGeoJsonData(state.countries),
    pickable: true,
    stroked: false,
    filled: true,
    extruded: false,
    getFillColor: (f: unknown) => getPolicyStateColor(policyStateOf(f as MapFeature), 110),
    opacity: 0.5,
    updateTriggers: { getFillColor: [state.countries] },
    ...entityPickHandlers(ctx, DECK_LAYER_IDS.countryPolicy, 'country'),
  });
}

/** Presence-cell density (aggregate-only; never individual points). */
export function buildPresenceCellLayer(ctx: LayerBuildContext): Layer | null {
  const { state } = ctx;
  if (!layerActive(state, DECK_LAYER_IDS.presenceCells)) return null;
  return new GeoJsonLayer({
    id: DECK_LAYER_IDS.presenceCells,
    data: asGeoJsonData(state.presenceCells),
    pickable: true,
    stroked: false,
    filled: true,
    extruded: false,
    getFillColor: (f: unknown) => presenceColor(f as MapFeature),
    opacity: 0.75,
    updateTriggers: { getFillColor: [state.presenceCells] },
    ...entityPickHandlers(ctx, DECK_LAYER_IDS.presenceCells, 'presenceCell'),
  });
}

/** Execution-rule boundaries (poll layer). Colour by rule status. */
export function buildExecutionRuleLayer(ctx: LayerBuildContext): Layer | null {
  const { state } = ctx;
  if (!layerActive(state, DECK_LAYER_IDS.executionRules)) return null;
  return new GeoJsonLayer({
    id: DECK_LAYER_IDS.executionRules,
    data: asGeoJsonData(state.executionRules),
    pickable: true,
    stroked: true,
    filled: true,
    extruded: false,
    getFillColor: (f: unknown) => {
      const c = ruleColor(f as MapFeature);
      return [c[0], c[1], c[2], 70] as RGBA; // translucent fill
    },
    getLineColor: (f: unknown) => ruleColor(f as MapFeature),
    getLineWidth: 2,
    lineWidthUnits: 'pixels',
    lineWidthMinPixels: 1,
    pointRadiusUnits: 'pixels',
    getPointRadius: 6,
    pointRadiusMinPixels: 4,
    opacity: 0.9,
    updateTriggers: { getFillColor: [state.executionRules], getLineColor: [state.executionRules] },
    ...entityPickHandlers(ctx, DECK_LAYER_IDS.executionRules, 'executionRule'),
  });
}

/** Point-entity scatter builder shared by agents/drones. */
function buildPointEntityLayer(
  ctx: LayerBuildContext,
  layerId: string,
  entityType: MapEntityType,
  slice: MapFeature[],
  color: RGBA,
  radiusPx: number,
): Layer | null {
  const { state } = ctx;
  if (!layerActive(state, layerId)) return null;
  const data = slice.filter(f => pointCoord(f) != null);
  return new ScatterplotLayer({
    id: layerId,
    data,
    pickable: true,
    stroked: true,
    filled: true,
    radiusUnits: 'pixels',
    getPosition: (f: unknown) => pointCoord(f as MapFeature) ?? [0, 0],
    getRadius: radiusPx,
    radiusMinPixels: 3,
    getFillColor: color,
    getLineColor: [255, 255, 255, 200],
    lineWidthUnits: 'pixels',
    getLineWidth: 1,
    ...entityPickHandlers(ctx, layerId, entityType),
  });
}

/** Agent positions (live layer). */
export function buildAgentLayer(ctx: LayerBuildContext): Layer | null {
  return buildPointEntityLayer(
    ctx, DECK_LAYER_IDS.agentPositions, 'agent', ctx.state.agents,
    [...SEMANTIC_RGB.active, 230] as RGBA, 6,
  );
}

/** Drone positions (live layer). */
export function buildDroneLayer(ctx: LayerBuildContext): Layer | null {
  return buildPointEntityLayer(
    ctx, DECK_LAYER_IDS.dronePositions, 'drone', ctx.state.drones,
    [...SEMANTIC_RGB.warning, 230] as RGBA, 5,
  );
}

/**
 * Build every currently-visible deck layer, in paint order (bottom →
 * top): policy choropleth and presence density sit under the spatial
 * objects; live point layers (drones, agents) paint on top.
 */
export function buildDeckLayers(ctx: LayerBuildContext): Layer[] {
  const includeSpatial = ctx.renderSpatialObjects !== false;
  const builders: Array<(c: LayerBuildContext) => Layer | null> = [
    buildCountryPolicyLayer,
    buildPresenceCellLayer,
    buildExecutionRuleLayer,
    ...(includeSpatial ? [buildSpatialObjectLayer] : []),
    buildDroneLayer,
    buildAgentLayer,
  ];
  const layers: Layer[] = [];
  for (const build of builders) {
    const l = build(ctx);
    if (l) layers.push(l);
  }
  return layers;
}
